<template>
  <div class="d-flex flex-column">
    <span class="statement-label">Histórico</span>
    <span v-if="!history || history.length <= 0"> Não há histórico de vouchers do Zé Compensa para esse consumidor.</span>

    <div v-else>
      <v-data-table :headers="headers" :items="history" disable-sort mobile-breakpoint="0" disable-pagination hide-default-footer>
        <template v-for="(h, index) in headers" v-slot:[`header.${h.value}`]>
          <span :key="`header-${index}`" class="voucher-ze-history-table-header">{{ h.text }}</span>
        </template>

        <template v-slot:item.voucher="{ item }">
          <div class="d-flex flex-row align-center" style="gap: 12px">
            <div class="img-container">
              <img :src="item.benefit.image" />
            </div>
            <span class="voucher-item-value cinza-0-text">R${{ localeString(item.benefit.value, 0) }}</span>
          </div>
        </template>

        <template v-slot:item.campaign="{ item }">
          <span v-if="item.offersSchedule" class="no-wrap-text" :class="{ link: item.offersSchedule.id }" @click="goToCampaign(item.offersSchedule.id)">
            {{ item.offersSchedule.name }}
          </span>
          <span v-else class="cinza-0-text no-wrap-text">-</span>
        </template>

        <template v-slot:item.status="{ item }">
          <span :class="`voucher-item-value item-${item.status.toLowerCase()}`">{{ getStatus(item.status) }}</span>
        </template>

        <template v-slot:item.dates="{ item }">
          <div class="d-flex flex-column cinza-0-text no-wrap-text">
            <span>Gerado em: {{ formatDate(item.activatedAt) }}</span>
            <span v-if="item.status === 'CONSUMED'">Consumido em: {{ formatDate(item.consumedAt) }}</span>
            <span v-else-if="item.status === 'EXPIRED'">Expirado em: {{ formatDate(item.expiresAt) }}</span>
          </div>
        </template>

        <template v-slot:item.coupon="{ item }">
          <div v-if="item.couponId" class="coupon-container">
            <copy-button tooltip="Copiar id" @click="copy(item.couponId)" />
            <span class="link" @click="openCouponDetails(item)">
              {{ item.couponId }}
            </span>
          </div>
          <span v-else-if="item.status !== 'EXPIRED' && !item.couponId" class="cinza-0-text no-wrap-text">Cupom em validação</span>
          <span v-else-if="!item.couponId" class="cinza-0-text no-wrap-text">-</span>
        </template>

        <template v-slot:item.unit.name="{ item }">
          <span class="cinza-0-text no-wrap-text">{{ item.unit && item.unit.name ? item.unit.name : '-' }}</span>
        </template>
      </v-data-table>
    </div>

    <coupon-modal v-model="showCouponModal" :coupon="selectedCoupon" :client-id="userId" />
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { localeString } from '@/config/helpers.js'
import CopyButton from '@/components/CopyButton.vue'
import CouponModal from '@/modules/clientes/detalhes-cliente/views/components/CouponModal.vue'

export default {
  name: 'CashbackHistoryTable',
  components: {
    CopyButton,
    CouponModal,
  },
  props: {
    history: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    showCouponModal: false,
    selectedCoupon: {},
    userId: '',
  }),
  computed: {
    headers() {
      return [
        {
          text: 'Voucher',
          align: 'start',
          value: 'voucher',
        },
        {
          text: 'Campanha',
          align: 'start',
          width: '20%',
          value: 'campaign',
        },
        {
          text: 'Status',
          align: 'start',
          value: 'status',
        },
        {
          text: 'Datas',
          align: 'start',
          value: 'dates',
        },
        {
          text: 'Cupom fiscal',
          align: 'start',
          value: 'coupon',
        },
        {
          text: 'Loja',
          align: 'start',
          value: 'unit.name',
        },
      ]
    },
  },
  methods: {
    localeString,
    formatDate(date) {
      if (!date) return '-'
      return DateTime.fromISO(date).toFormat('dd/MM/yyyy - HH:mm')
    },
    getStatus(status) {
      switch (status) {
        case 'ACTIVATED':
          return 'Ativado'
        case 'CONSUMED':
          return 'Consumido'
        case 'EXPIRED':
          return 'Expirado'
        default:
          return '-'
      }
    },
    async goToCampaign(offerScheduleId) {
      if (!offerScheduleId) return

      if (this.isMobile) {
        const confirmed = await this.$confirm({
          title: 'Visualização não otimizada para dispositivos móveis',
          message:
            'A página para o qual você será redirecionado não possui visualização otimizada para dispositivos móveis. É recomendado utilizar um desktop. Deseja continuar mesmo assim?',
          confirmText: 'Continuar',
          cancelText: 'Cancelar',
        })
          .then(() => true)
          .catch(() => false)
        if (!confirmed) {
          return
        }
      }

      const route = this.$router.resolve({ name: 'ofertas-trade-resultado-mercapromo', params: { id: offerScheduleId } })
      window.open(route.href, '_blank')
    },
    openCouponDetails(item) {
      this.selectedCoupon = { id: item.couponId }
      this.userId = item.user.documentNo
      this.showCouponModal = true
    },
    copy(text) {
      navigator.clipboard.writeText(text)
    },
  },
}
</script>

<style lang="scss" scoped>
.statement-label {
  font-size: 16px;
  font-weight: 700;
  color: $cinza-d10;
}

.img-container {
  margin: 6px 0;
  border: 1px dashed #c4baf7;
  border-radius: 4px;
  width: 50px;

  & img {
    display: block;
    width: 48px;
    height: 48px;
    object-fit: contain;
  }
}

.no-wrap-text {
  white-space: nowrap;
}

.voucher-ze-history-table-header {
  font-size: 14px;
  font-weight: 700;
  color: $cinza-d20;
}

.voucher-item-value {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}

.item-expired {
  color: $vermelho-d20;
}

.item-activated {
  color: $verde-d20;
}

.item-consumed {
  color: $marca-0-l20;
}

.cinza-0-text {
  color: $cinza-0;
}

.cinza-l10-text {
  color: $cinza-l10;
}

.coupon-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  min-width: 298px;
}
</style>
